import { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";

import { useCurrentUser } from "@/auth/RequireAuth";
import { useAuthedApi } from "@/api/useAuthedApi";
import { PlanStep } from "@/routes/onboarding/PlanStep";
import { OnboardingStateProvider } from "@/routes/onboarding/useOnboardingState";

/** Reactivation screen for users with lapsed subscriptions.
 *
 *  - past_due / unpaid → "Update payment method" via Stripe portal redirect
 *  - canceled → "Choose a plan" → reuse PlanStep
 */
export function Reactivate() {
  const user = useCurrentUser();
  const { billing } = useAuthedApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = user?.subscription_status;

  // If subscription is active/sponsored, redirect to workspace
  useEffect(() => {
    if (status === "active" || status === "sponsored") {
      window.location.assign("/workspace");
    }
  }, [status]);

  async function handlePortalRedirect() {
    setLoading(true);
    setError(null);
    try {
      const { url } = await billing.portal(window.location.href);
      window.location.assign(url);
    } catch (e) {
      setError((e as Error).message || "Failed to open billing portal");
      setLoading(false);
    }
  }

  if (!status) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // past_due or unpaid → portal redirect to update payment
  if (status === "past_due" || status === "unpaid") {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh", p: 3 }}>
        <Stack spacing={3} sx={{ maxWidth: 480, textAlign: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Payment issue
          </Typography>
          <Typography color="text.secondary">
            Your subscription payment failed. Please update your payment method to
            continue using Artie.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Button
            variant="contained"
            size="large"
            onClick={handlePortalRedirect}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Update Payment Method"}
          </Button>
        </Stack>
      </Box>
    );
  }

  // canceled → re-subscribe with PlanStep
  return (
    <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh", p: 3 }}>
      <Stack spacing={3} sx={{ maxWidth: 560 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, textAlign: "center" }}>
          Reactivate your subscription
        </Typography>
        <Typography color="text.secondary" sx={{ textAlign: "center" }}>
          Your subscription has been canceled. Choose a plan to continue using Artie.
        </Typography>
        <OnboardingStateProvider>
          <PlanStep />
        </OnboardingStateProvider>
      </Stack>
    </Box>
  );
}
